import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Relationship, RelationshipFormData, RelationshipValidation, RelationshipErrorCode } from '../types';
import { ChangeTrackingService } from './changeTrackingService';

/**
 * RelationshipService - Handles all relationship operations with Firestore
 * Implements relationship management with validation and circular relationship detection
 */
export class RelationshipService {
  private static readonly COLLECTION_NAME = 'relationships';

  /**
   * Create a new relationship
   */
  static async createRelationship(
    relationshipData: RelationshipFormData,
    userId: string,
    userName: string = 'Unknown User',
    isAdmin: boolean = false
  ): Promise<Relationship> {
    try {
      // Validate the relationship
      const validation = await this.validateRelationship(relationshipData);
      if (!validation.isValid) {
        throw new Error(`Validation failed: ${validation.errors.map((e: any) => e.message).join(', ')}`);
      }

      // Check for circular relationships
      const circularCheck = await this.checkCircularRelationship(
        relationshipData.personId1,
        relationshipData.personId2,
        relationshipData.type
      );
      if (circularCheck.hasCircular) {
        throw new Error(`Circular relationship detected: ${circularCheck.message}`);
      }

      // Prepare relationship object
      const relationshipDoc = {
        personId1: relationshipData.personId1,
        personId2: relationshipData.personId2,
        type: relationshipData.type,
        marriageDate: relationshipData.marriageDate || undefined,
        marriagePlace: relationshipData.marriagePlace || undefined,
        divorceDate: relationshipData.divorceDate || undefined,
        divorcePlace: relationshipData.divorcePlace || undefined,
        marriageEndReason: relationshipData.marriageEndReason || undefined,
        adoptionDate: relationshipData.adoptionDate || undefined,
        isAdopted: relationshipData.isAdopted || false,
        isBiological: relationshipData.isBiological ?? true,
        isStepRelationship: relationshipData.isStepRelationship || false,
        notes: relationshipData.notes || undefined,
        sources: relationshipData.sources ? relationshipData.sources.split(',').map((s: string) => s.trim()).filter((s: string) => s) : [],
        isConfirmed: false,
        confidence: 80,
        isApproved: false,
        createdBy: userId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      // Create document in Firestore
      const docRef = await addDoc(collection(db, this.COLLECTION_NAME), relationshipDoc);

      // Update document with its own ID
      await updateDoc(docRef, { id: docRef.id });

      // Build the created relationship object
      const createdRelationship: Relationship = {
        ...relationshipDoc,
        id: docRef.id,
        createdAt: new Date(),
        updatedAt: new Date(),
      } as Relationship;

      // Log change to ChangeTrackingService
      try {
        await ChangeTrackingService.logChange(
          {
            entityType: 'relationship',
            entityId: docRef.id,
            changeType: 'create',
            beforeState: null,
            afterState: createdRelationship,
            changedFields: Object.keys(relationshipDoc),
            reason: `Relationship created: ${relationshipData.type} between ${relationshipData.personId1} and ${relationshipData.personId2}`,
          },
          userId,
          userName,
          isAdmin,
          'user' // Source: manual user edit
        );
      } catch (changeLogError: any) {
        console.error('Failed to log change (non-blocking):', changeLogError);
        // Don't throw - change logging is non-critical
      }

      // Return the created relationship
      return createdRelationship;
    } catch (error: any) {
      throw new Error(`Failed to create relationship: ${error.message}`);
    }
  }

  /**
   * Get all relationships for a person
   */
  static async getRelationshipsForPerson(personId: string): Promise<Relationship[]> {
    try {
      // Query relationships where person is either personId1 or personId2
      const q1 = query(
        collection(db, this.COLLECTION_NAME),
        where('personId1', '==', personId),
        where('isDeleted', '!=', true),
        orderBy('isDeleted'),
        orderBy('createdAt', 'desc')
      );

      const q2 = query(
        collection(db, this.COLLECTION_NAME),
        where('personId2', '==', personId),
        where('isDeleted', '!=', true),
        orderBy('isDeleted'),
        orderBy('createdAt', 'desc')
      );

      const [snapshot1, snapshot2] = await Promise.all([getDocs(q1), getDocs(q2)]);
      
      const relationships: Relationship[] = [];
      
      // Process results from both queries
      [...snapshot1.docs, ...snapshot2.docs].forEach(doc => {
        const data = doc.data();
        
        // Convert Firestore timestamps to Date objects, handle both Timestamp and string formats
        const createdAt = data.createdAt
          ? (typeof data.createdAt === 'string' ? new Date(data.createdAt) : data.createdAt.toDate?.() || new Date(data.createdAt))
          : new Date();
        
        const updatedAt = data.updatedAt
          ? (typeof data.updatedAt === 'string' ? new Date(data.updatedAt) : data.updatedAt.toDate?.() || new Date(data.updatedAt))
          : new Date();

        relationships.push({
          ...data,
          id: doc.id,
          createdAt,
          updatedAt,
        } as Relationship);
      });

      // Remove duplicates and sort by creation date
      const uniqueRelationships = relationships.filter((rel, index, arr) => 
        arr.findIndex(r => r.id === rel.id) === index
      );

      return uniqueRelationships.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
    } catch (error: any) {
      // Handle specific Firebase errors gracefully
      if (error.code === 'permission-denied' ||
          error.code === 'not-found' ||
          error.code === 'failed-precondition' ||
          error.message?.includes('collection') ||
          error.message?.includes('Missing or insufficient permissions') ||
          error.message?.includes('requires an index')) {
        // Return empty array for permission issues, missing collections, or index errors
        return [];
      }
      
      // Re-throw unexpected errors
      throw new Error(`Failed to get relationships: ${error.message}`);
    }
  }

  /**
   * Update an existing relationship
   */
  static async updateRelationship(
    relationshipId: string,
    updates: Partial<Relationship>,
    userId: string,
    userName: string = 'Unknown User',
    isAdmin: boolean = false
  ): Promise<void> {
    try {
      const relationshipRef = doc(db, this.COLLECTION_NAME, relationshipId);

      // Verify relationship exists
      const relationshipDoc = await getDoc(relationshipRef);
      if (!relationshipDoc.exists()) {
        throw new Error('Relationship not found');
      }

      // Get before state for change tracking
      const beforeState = {
        id: relationshipDoc.id,
        ...relationshipDoc.data(),
      } as Relationship;

      // If updating person IDs or type, validate for circular relationships
      if (updates.personId1 || updates.personId2 || updates.type) {
        const currentData = relationshipDoc.data() as Relationship;
        const personId1 = updates.personId1 || currentData.personId1;
        const personId2 = updates.personId2 || currentData.personId2;
        const type = updates.type || currentData.type;

        const circularCheck = await this.checkCircularRelationship(personId1, personId2, type, relationshipId);
        if (circularCheck.hasCircular) {
          throw new Error(`Circular relationship detected: ${circularCheck.message}`);
        }
      }

      // Prepare update object
      const updateData = {
        ...updates,
        updatedAt: serverTimestamp(),
        updatedBy: userId,
      };

      // Remove undefined values
      Object.keys(updateData).forEach(key => {
        if ((updateData as any)[key] === undefined) {
          delete (updateData as any)[key];
        }
      });

      await updateDoc(relationshipRef, updateData);

      // Get after state for change tracking
      const afterState = {
        ...beforeState,
        ...updateData,
        updatedAt: new Date(),
      } as Relationship;

      // Log change to ChangeTrackingService
      try {
        await ChangeTrackingService.logChange(
          {
            entityType: 'relationship',
            entityId: relationshipId,
            changeType: 'update',
            beforeState,
            afterState,
            changedFields: Object.keys(updates),
            reason: `Relationship updated: ${beforeState.type}`,
          },
          userId,
          userName,
          isAdmin,
          'user' // Source: manual user edit
        );
      } catch (changeLogError: any) {
        console.error('Failed to log change (non-blocking):', changeLogError);
        // Don't throw - change logging is non-critical
      }
    } catch (error: any) {
      throw new Error(`Failed to update relationship: ${error.message}`);
    }
  }

  /**
   * Delete a relationship (soft delete)
   */
  static async deleteRelationship(
    relationshipId: string,
    userId: string,
    userName: string = 'Unknown User',
    isAdmin: boolean = false
  ): Promise<void> {
    try {
      const relationshipRef = doc(db, this.COLLECTION_NAME, relationshipId);

      // Get relationship data before deletion for change tracking
      const relationshipDoc = await getDoc(relationshipRef);
      if (!relationshipDoc.exists()) {
        throw new Error('Relationship not found');
      }

      const beforeState = {
        id: relationshipDoc.id,
        ...relationshipDoc.data(),
      } as Relationship;

      // Soft delete by marking as deleted
      await updateDoc(relationshipRef, {
        isDeleted: true,
        deletedAt: serverTimestamp(),
        deletedBy: userId,
      });

      // Log change to ChangeTrackingService
      try {
        await ChangeTrackingService.logChange(
          {
            entityType: 'relationship',
            entityId: relationshipId,
            changeType: 'delete',
            beforeState,
            afterState: null,
            changedFields: ['isDeleted', 'deletedAt', 'deletedBy'],
            reason: `Relationship deleted: ${beforeState.type}`,
          },
          userId,
          userName,
          isAdmin,
          'user' // Source: manual user edit
        );
      } catch (changeLogError: any) {
        console.error('Failed to log change (non-blocking):', changeLogError);
        // Don't throw - change logging is non-critical
      }
    } catch (error: any) {
      throw new Error(`Failed to delete relationship: ${error.message}`);
    }
  }

  /**
   * Get relationship by ID
   */
  static async getRelationshipById(relationshipId: string): Promise<Relationship | null> {
    try {
      const relationshipDoc = await getDoc(doc(db, this.COLLECTION_NAME, relationshipId));
      
      if (!relationshipDoc.exists()) {
        return null;
      }

      const data = relationshipDoc.data();
      
      // Convert Firestore timestamps to Date objects, handle both Timestamp and string formats
      const createdAt = data.createdAt
        ? (typeof data.createdAt === 'string' ? new Date(data.createdAt) : data.createdAt.toDate?.() || new Date(data.createdAt))
        : new Date();
      
      const updatedAt = data.updatedAt
        ? (typeof data.updatedAt === 'string' ? new Date(data.updatedAt) : data.updatedAt.toDate?.() || new Date(data.updatedAt))
        : new Date();

      return {
        ...data,
        id: relationshipDoc.id,
        createdAt,
        updatedAt,
      } as Relationship;
    } catch (error: any) {
      // Handle specific Firebase errors gracefully
      if (error.code === 'permission-denied' ||
          error.code === 'not-found' ||
          error.message?.includes('collection') ||
          error.message?.includes('Missing or insufficient permissions')) {
        // Return null for permission issues or missing collections
        return null;
      }
      
      // Re-throw unexpected errors
      throw new Error(`Failed to get relationship: ${error.message}`);
    }
  }

  /**
   * Validate relationship data
   */
  static async validateRelationship(data: RelationshipFormData): Promise<RelationshipValidation> {
    const errors: Array<{ code: RelationshipErrorCode; field: string; message: string }> = [];
    const warnings: Array<{ code: string; field: string; message: string; suggestion?: string }> = [];

    // Required fields
    if (!data.personId1) {
      errors.push({ code: 'MISSING_REQUIRED_FIELD', field: 'personId1', message: 'First person is required' });
    }

    if (!data.personId2) {
      errors.push({ code: 'MISSING_REQUIRED_FIELD', field: 'personId2', message: 'Second person is required' });
    }

    if (!data.type) {
      errors.push({ code: 'MISSING_REQUIRED_FIELD', field: 'type', message: 'Relationship type is required' });
    }

    // Cannot relate person to themselves
    if (data.personId1 === data.personId2) {
      errors.push({ code: 'INVALID_RELATIONSHIP_TYPE', field: 'personId2', message: 'Cannot create relationship between person and themselves' });
    }

    // Date validation
    if (data.marriageDate && data.divorceDate) {
      const marriageDate = new Date(data.marriageDate);
      const divorceDate = new Date(data.divorceDate);
      
      if (divorceDate <= marriageDate) {
        errors.push({ code: 'INVALID_DATE_RANGE', field: 'divorceDate', message: 'Divorce date must be after marriage date' });
      }
    }

    // Future date warnings
    if (data.marriageDate) {
      const marriageDate = new Date(data.marriageDate);
      const now = new Date();
      
      if (marriageDate > now) {
        warnings.push({
          code: 'FUTURE_DATE',
          field: 'marriageDate',
          message: 'Marriage date is in the future',
          suggestion: 'Verify the date is correct'
        });
      }
    }

    // Check for duplicate relationships
    if (data.personId1 && data.personId2 && data.type) {
      const existingRelationships = await this.getRelationshipsForPerson(data.personId1);
      const duplicate = existingRelationships.find(rel =>
        ((rel.personId1 === data.personId1 && rel.personId2 === data.personId2) ||
         (rel.personId1 === data.personId2 && rel.personId2 === data.personId1)) &&
        rel.type === data.type &&
        rel.isConfirmed
      );

      if (duplicate) {
        errors.push({ code: 'DUPLICATE_RELATIONSHIP', field: 'type', message: 'This relationship already exists' });
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
    };
  }

  /**
   * Check for circular relationships in the family tree
   */
  static async checkCircularRelationship(
    person1Id: string,
    person2Id: string,
    relationshipType: string,
    excludeRelationshipId?: string
  ): Promise<{ hasCircular: boolean; message?: string }> {
    try {
      // Only check for parent-child relationships which can create cycles
      if (!['parent', 'child'].includes(relationshipType)) {
        return { hasCircular: false };
      }

      // Get all relationships to build a graph
      const allRelationships = await this.getAllActiveRelationships();
      
      // Filter out the relationship being updated if provided
      const relationships = excludeRelationshipId
        ? allRelationships.filter(rel => rel.id !== excludeRelationshipId)
        : allRelationships;

      // Build adjacency list for parent-child relationships
      const parentChildGraph = new Map<string, Set<string>>();
      
      relationships.forEach(rel => {
        if (rel.type === 'parent') {
          if (!parentChildGraph.has(rel.personId1)) {
            parentChildGraph.set(rel.personId1, new Set());
          }
          parentChildGraph.get(rel.personId1)!.add(rel.personId2);
        } else if (rel.type === 'child') {
          if (!parentChildGraph.has(rel.personId2)) {
            parentChildGraph.set(rel.personId2, new Set());
          }
          parentChildGraph.get(rel.personId2)!.add(rel.personId1);
        }
      });

      // Add the new relationship to the graph
      if (relationshipType === 'parent') {
        if (!parentChildGraph.has(person1Id)) {
          parentChildGraph.set(person1Id, new Set());
        }
        parentChildGraph.get(person1Id)!.add(person2Id);
      } else if (relationshipType === 'child') {
        if (!parentChildGraph.has(person2Id)) {
          parentChildGraph.set(person2Id, new Set());
        }
        parentChildGraph.get(person2Id)!.add(person1Id);
      }

      // Use DFS to detect cycles
      const visited = new Set<string>();
      const recursionStack = new Set<string>();

      const hasCycle = (nodeId: string): boolean => {
        if (recursionStack.has(nodeId)) {
          return true; // Back edge found - cycle detected
        }
        
        if (visited.has(nodeId)) {
          return false; // Already processed this node
        }

        visited.add(nodeId);
        recursionStack.add(nodeId);

        const children = parentChildGraph.get(nodeId) || new Set();
        const childrenArray = Array.from(children);
        for (let i = 0; i < childrenArray.length; i++) {
          if (hasCycle(childrenArray[i])) {
            return true;
          }
        }

        recursionStack.delete(nodeId);
        return false;
      };

      // Check for cycles starting from any node
      const nodeIds = Array.from(parentChildGraph.keys());
      for (let i = 0; i < nodeIds.length; i++) {
        const nodeId = nodeIds[i];
        if (!visited.has(nodeId)) {
          if (hasCycle(nodeId)) {
            return {
              hasCircular: true,
              message: 'This relationship would create a circular family tree'
            };
          }
        }
      }

      return { hasCircular: false };
    } catch (error: any) {
      // If we can't check for circular relationships, allow the operation but log the error
      console.error('Error checking circular relationships:', error);
      return { hasCircular: false };
    }
  }

  /**
   * Get all active relationships (helper for circular relationship detection)
   */
  private static async getAllActiveRelationships(): Promise<Relationship[]> {
    try {
      const q = query(
        collection(db, this.COLLECTION_NAME),
        where('isDeleted', '!=', true),
        where('isActive', '==', true),
        orderBy('isDeleted')
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => {
        const data = doc.data();
        
        // Convert Firestore timestamps to Date objects, handle both Timestamp and string formats
        const createdAt = data.createdAt
          ? (typeof data.createdAt === 'string' ? new Date(data.createdAt) : data.createdAt.toDate?.() || new Date(data.createdAt))
          : new Date();
        
        const updatedAt = data.updatedAt
          ? (typeof data.updatedAt === 'string' ? new Date(data.updatedAt) : data.updatedAt.toDate?.() || new Date(data.updatedAt))
          : new Date();

        return {
          ...data,
          id: doc.id,
          createdAt,
          updatedAt,
        } as Relationship;
      });
    } catch (error: any) {
      // Handle specific Firebase errors gracefully
      if (error.code === 'permission-denied' ||
          error.code === 'not-found' ||
          error.code === 'failed-precondition' ||
          error.message?.includes('collection') ||
          error.message?.includes('Missing or insufficient permissions') ||
          error.message?.includes('requires an index')) {
        // Return empty array for permission issues, missing collections, or index errors
        return [];
      }
      
      // Re-throw unexpected errors but don't break circular detection
      console.error('Error getting active relationships:', error);
      return [];
    }
  }

  /**
   * Get relationship summary for a person (parents, children, spouses, siblings)
   */
  static async getRelationshipSummary(personId: string): Promise<{
    parents: string[];
    children: string[];
    spouses: string[];
    siblings: string[];
  }> {
    try {
      const relationships = await this.getRelationshipsForPerson(personId);
      
      const summary = {
        parents: [] as string[],
        children: [] as string[],
        spouses: [] as string[],
        siblings: [] as string[],
      };

      relationships.forEach(rel => {
        if (!rel.isConfirmed) return;

        const otherPersonId = rel.personId1 === personId ? rel.personId2 : rel.personId1;
        
        switch (rel.type) {
          case 'parent':
            if (rel.personId2 === personId) {
              // personId is the child
              summary.parents.push(rel.personId1);
            } else {
              // personId is the parent
              summary.children.push(rel.personId2);
            }
            break;
          case 'child':
            if (rel.personId1 === personId) {
              // personId is the child
              summary.parents.push(rel.personId2);
            } else {
              // personId is the parent
              summary.children.push(rel.personId1);
            }
            break;
          case 'spouse':
            summary.spouses.push(otherPersonId);
            break;
          case 'sibling':
            summary.siblings.push(otherPersonId);
            break;
        }
      });

      return summary;
    } catch (error: any) {
      // Handle specific Firebase errors gracefully
      if (error.code === 'permission-denied' ||
          error.code === 'not-found' ||
          error.code === 'failed-precondition' ||
          error.message?.includes('collection') ||
          error.message?.includes('Missing or insufficient permissions') ||
          error.message?.includes('requires an index')) {
        // Return empty summary for permission issues, missing collections, or index errors
        return {
          parents: [],
          children: [],
          spouses: [],
          siblings: [],
        };
      }
      
      // Re-throw unexpected errors
      throw new Error(`Failed to get relationship summary: ${error.message}`);
    }
  }

  /**
   * Get person relationships summary with PersonSummary objects
   */
  static async getPersonRelationships(personId: string): Promise<import('../types').PersonRelationshipSummary> {
    try {
      const relationships = await this.getRelationshipsForPerson(personId);
      const { PersonService } = await import('./personService');
      
      const summary = {
        personId,
        parents: [] as import('../types').PersonSummary[],
        children: [] as import('../types').PersonSummary[],
        spouses: [] as import('../types').PersonSummary[],
        siblings: [] as import('../types').PersonSummary[],
      };

      // Collect all related person IDs
      const relatedPersonIds = new Set<string>();
      
      relationships.forEach(rel => {
        if (!rel.isConfirmed && !rel.isApproved) return;

        const otherPersonId = rel.personId1 === personId ? rel.personId2 : rel.personId1;
        relatedPersonIds.add(otherPersonId);
      });

      // Fetch all related persons in parallel
      const relatedPersons = await Promise.all(
        Array.from(relatedPersonIds).map(id => PersonService.getPersonById(id))
      );

      // Create a map for quick lookup
      const personMap = new Map<string, import('../types').Person>();
      relatedPersons.forEach((person: import('../types').Person | null) => {
        if (person) {
          personMap.set(person.id, person);
        }
      });

      // Convert to PersonSummary and categorize
      relationships.forEach(rel => {
        if (!rel.isConfirmed && !rel.isApproved) return;

        const otherPersonId = rel.personId1 === personId ? rel.personId2 : rel.personId1;
        const person = personMap.get(otherPersonId);
        
        if (!person) return;

        const personSummary: import('../types').PersonSummary = {
          id: person.id,
          firstName: person.firstName,
          lastName: person.lastName,
          maidenName: person.maidenName,
          birthDate: person.birthDate,
          deathDate: person.deathDate,
          isDeceased: person.isDeceased,
          primaryPhotoUrl: person.primaryPhotoId,
          gender: person.gender,
        };

        switch (rel.type) {
          case 'parent':
            if (rel.personId2 === personId) {
              // personId is the child
              summary.parents.push(personSummary);
            } else {
              // personId is the parent
              summary.children.push(personSummary);
            }
            break;
          case 'child':
            if (rel.personId1 === personId) {
              // personId is the child
              summary.parents.push(personSummary);
            } else {
              // personId is the parent
              summary.children.push(personSummary);
            }
            break;
          case 'spouse':
            summary.spouses.push(personSummary);
            break;
          case 'sibling':
            summary.siblings.push(personSummary);
            break;
        }
      });

      return summary;
    } catch (error: any) {
      // Handle specific Firebase errors gracefully
      if (error.code === 'permission-denied' ||
          error.code === 'not-found' ||
          error.code === 'failed-precondition' ||
          error.message?.includes('collection') ||
          error.message?.includes('Missing or insufficient permissions') ||
          error.message?.includes('requires an index')) {
        // Return empty summary for permission issues, missing collections, or index errors
        return {
          personId,
          parents: [],
          children: [],
          spouses: [],
          siblings: [],
        };
      }
      
      // Re-throw unexpected errors
      throw new Error(`Failed to get person relationships: ${error.message}`);
    }
  }
  /**
   * Get relationships with detailed person information for display
   */
  static async getRelationshipsWithPersonDetails(personId: string): Promise<Array<{
    relationship: Relationship;
    relatedPerson: import('../types').Person | null;
    relationshipLabel: string;
  }>> {
    try {
      const relationships = await this.getRelationshipsForPerson(personId);
      const { PersonService } = await import('./personService');
      
      const relationshipsWithDetails = await Promise.all(
        relationships.map(async (relationship) => {
          const otherPersonId = relationship.personId1 === personId
            ? relationship.personId2
            : relationship.personId1;
          
          const relatedPerson = await PersonService.getPersonById(otherPersonId);
          
          // Determine the relationship label from the current person's perspective
          let relationshipLabel = relationship.type;
          if (relationship.type === 'parent' && relationship.personId2 === personId) {
            relationshipLabel = 'parent';
          } else if (relationship.type === 'parent' && relationship.personId1 === personId) {
            relationshipLabel = 'child';
          } else if (relationship.type === 'child' && relationship.personId1 === personId) {
            relationshipLabel = 'parent';
          } else if (relationship.type === 'child' && relationship.personId2 === personId) {
            relationshipLabel = 'child';
          }
          
          return {
            relationship,
            relatedPerson,
            relationshipLabel,
          };
        })
      );
      
      return relationshipsWithDetails.filter(item => item.relatedPerson !== null);
    } catch (error: any) {
      console.error('Error getting relationships with person details:', error);
      return [];
    }
  }

  /**
   * Bulk update relationships (useful for relationship management operations)
   */
  static async updateMultipleRelationships(
    updates: Array<{ relationshipId: string; updates: Partial<Relationship> }>,
    userId: string
  ): Promise<void> {
    try {
      await Promise.all(
        updates.map(({ relationshipId, updates: relationshipUpdates }) =>
          this.updateRelationship(relationshipId, relationshipUpdates, userId)
        )
      );
    } catch (error: any) {
      throw new Error(`Failed to update multiple relationships: ${error.message}`);
    }
  }

  /**
   * Get relationship statistics for a person
   */
  static async getRelationshipStats(personId: string): Promise<{
    total: number;
    confirmed: number;
    pending: number;
    byType: Record<string, number>;
  }> {
    try {
      const relationships = await this.getRelationshipsForPerson(personId);
      
      const stats = {
        total: relationships.length,
        confirmed: relationships.filter(r => r.isConfirmed).length,
        pending: relationships.filter(r => !r.isConfirmed).length,
        byType: {} as Record<string, number>,
      };
      
      relationships.forEach(rel => {
        stats.byType[rel.type] = (stats.byType[rel.type] || 0) + 1;
      });
      
      return stats;
    } catch (error: any) {
      console.error('Error getting relationship stats:', error);
      return {
        total: 0,
        confirmed: 0,
        pending: 0,
        byType: {},
      };
    }
  }
}

export default RelationshipService;